import { Loader } from '../../components/Loader';
import {
  Button,
  ButtonVariants,
  LinkButton,
  Modal,
  Typography,
} from '../../components/primitives';
import { testId } from '../../utils';
import { isUndefined } from '@blurb/utils';
import { useTranslation } from '@rpi/i18n';
import React, { useEffect } from 'react';
import { FieldError } from 'react-hook-form';

export interface FormElementProps {
  label: string;
  loading: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
  value?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  isToggle?: boolean;
  onChange?: (event: React.FormEvent<HTMLFormElement>) => void;
  success?: boolean;
  error?: FieldError;
  serverError?: string;
  'data-testid': string;
}

export const FormElement = ({
  label,
  value,
  title,
  children,
  isToggle,
  loading,
  onSubmit,
  onCancel,
  success,
  serverError,
  'data-testid': dataTestId,
}: FormElementProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'route.user.settings.account',
  });

  const [open, setOpen] = React.useState(false);
  useEffect(() => {
    if (success) setOpen(false);
  }, [success, setOpen]);

  const closeElement: React.ReactNode = (
    <Button
      size="sm"
      {...testId('form-element', 'cancel')}
      key="cancel"
      onClick={onCancel}
      variant={ButtonVariants.Secondary}
    >
      {t('cancel_button')}
    </Button>
  );
  const submitElement = (
    <Button
      size="sm"
      {...testId('form-element', 'save')}
      key="save"
      onClick={onSubmit}
    >
      {t('save_button')}
    </Button>
  );

  const triggerElement = (
    <LinkButton {...testId(dataTestId, 'form-element', 'edit')} type="button">
      {t('edit_button')}
    </LinkButton>
  );

  return (
    <div className="border-t last:border-b border-[#DEDEDE] h-16 flex items-center justify-between gap-8">
      <div>
        <Typography
          className="whitespace-nowrap"
          variant="body-label"
          {...testId('form-element', 'label')}
        >
          {label}
        </Typography>
      </div>
      {loading ? (
        <div className="flex justify-center min-h-[42px]">
          <Loader size="sm" className="text-brand" />
        </div>
      ) : (
        <>
          <div className="overflow-hidden whitespace-nowrap text-ellipsis flex-grow text-right">
            <Typography
              variant="body-dark"
              {...testId(dataTestId, 'form-element', 'value')}
            >
              {value}
            </Typography>
          </div>
          <div>
            {isToggle
              ? children
              : !isUndefined(children) && (
                  <Modal
                    {...testId('form-element', 'modal')}
                    title={title}
                    triggerElement={triggerElement}
                    contentElement={children}
                    closeElement={closeElement}
                    submitElement={submitElement}
                    open={open}
                    setOpen={setOpen}
                    serverError={serverError}
                  />
                )}
          </div>
        </>
      )}
    </div>
  );
};
